import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, Dropdown, Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import { generateExportHTML } from "@components/HtmlExportTemplate/htmlExportTemplate";

const FORMAT_OPTIONS = [
  { key: "csv", value: "csv", text: "CSV", icon: "table" },
  { key: "pdf", value: "pdf", text: "PDF", icon: "file pdf" },
];

const TRUNCATE_LIMITS = {
  name: 25,
  email: 30,
  default: 30,
};

export const ExportDataControls = ({
  data,
  filenamePrefix = "export",
  columns,
  reportTitle,
  reportType = "default",
  showUniversityLogo = true,
  showLrcLogo = true,
  footerText = "Library Management System Export",
}) => {
  const [selectedFormat, setSelectedFormat] = useState("csv");
  const [isLoading, setIsLoading] = useState(false);

  const createDownloadLink = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  const getCellValue = (item, col) => {
    return typeof col.accessor === "function"
      ? col.accessor(item)
      : item[col.accessor];
  };

  const truncateText = (text, maxLength = TRUNCATE_LIMITS.default) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const formatValueForPDF = (value, accessor) => {
    if (reportType === "finemanagement") {
      if (accessor === "name") return truncateText(value, TRUNCATE_LIMITS.name);
      if (accessor === "email")
        return truncateText(value, TRUNCATE_LIMITS.email);
      if (accessor === "totalFine")
        return typeof value === "number" ? value.toFixed(2) : value;
    }

    if (reportType === "finehistory" && accessor === "total_fine") {
      return typeof value === "number" ? value.toFixed(2) : value;
    }

    return value;
  };

  const exportCSV = () => {
    if (!data?.length) return;

    const headers = columns.map((col) => `"${col.header}"`).join(",");
    const rows = data.map((item) =>
      columns.map((col) => `"${getCellValue(item, col) ?? ""}"`).join(",")
    );

    const csvContent = [headers, ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    createDownloadLink(blob, `${filenamePrefix}_${Date.now()}.csv`);
  };

  const exportPDF = () => {
    if (!data?.length) return;

    const tableHeaders = columns
      .map((col) => `<th>${col.header}</th>`)
      .join("");
    const tableRows = data
      .map((item) => {
        const cells = columns
          .map((col) => {
            const value = getCellValue(item, col);
            const formattedValue = formatValueForPDF(value, col.accessor);
            return `<td>${formattedValue ?? ""}</td>`;
          })
          .join("");
        return `<tr>${cells}</tr>`;
      })
      .join("");

    const htmlContent = generateExportHTML({
      reportTitle: reportTitle || `${filenamePrefix} Export Report`,
      generatedDate: new Date().toLocaleString(),
      tableHeaders,
      tableRows,
      footerText,
      showUniversityLogo,
      showLrcLogo,
      reportType,
      fallbackLogoText: "Library Resource Center",
    });

    const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, "_blank");

    setTimeout(() => URL.revokeObjectURL(url), 2000);

    if (printWindow) {
      printWindow.onload = () => setTimeout(() => printWindow.focus(), 500);
    }
  };

  const handleExportClick = async () => {
    if (!data?.length) {
      toast.warning("No data to export for the selected year.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsLoading(true);

    if (selectedFormat === "csv") {
      exportCSV();
    } else if (selectedFormat === "pdf") {
      exportPDF();
    }

    setIsLoading(false);
  };

  const handleFormatChange = (e, { value }) => setSelectedFormat(value);

  const selectedIcon = FORMAT_OPTIONS.find(
    (opt) => opt.value === selectedFormat
  )?.icon;

  return (
    <Button.Group>
      <Dropdown
        button
        basic
        floating
        options={FORMAT_OPTIONS}
        value={selectedFormat}
        onChange={handleFormatChange}
        selectOnBlur={false}
        icon={null}
        trigger={
          <span>
            <Icon name={selectedIcon} />
            {selectedFormat.toUpperCase()}
          </span>
        }
      />
      <Button
        primary
        icon
        size="small"
        labelPosition="left"
        onClick={handleExportClick}
        loading={isLoading}
        disabled={isLoading}
      >
        <Icon name="download" />
        Export Results
      </Button>
    </Button.Group>
  );
};

ExportDataControls.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  filenamePrefix: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string.isRequired,
      accessor: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
        .isRequired,
    })
  ).isRequired,
  reportTitle: PropTypes.string,
  reportType: PropTypes.string,
  showUniversityLogo: PropTypes.bool,
  showLrcLogo: PropTypes.bool,
  footerText: PropTypes.string,
};

export default ExportDataControls;
